import React from 'react';
import { Search, X, Filter as FilterIcon } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';

/**
 * Reusable filter bar for admin lists.
 *
 * props:
 *  - search: current search string
 *  - onSearch: (value) => void
 *  - placeholder: input placeholder
 *  - chips: [{ key, label, value, options: [{value, label}] }] — each renders a <select>
 *  - onChipChange: (key, value) => void
 *  - dateRange: { from, to } | null  — if provided shows date inputs
 *  - onDateChange: ({from, to}) => void
 *  - onClear: () => void
 *  - resultCount: number (optional badge)
 */
export default function FilterBar({
  search = '',
  onSearch,
  placeholder = 'Search…',
  chips = [],
  onChipChange,
  dateRange = null,
  onDateChange,
  onClear,
  resultCount,
  testId = 'filter-bar'
}) {
  const hasFilter =
    !!search ||
    chips.some(c => c.value && c.value !== 'all') ||
    (dateRange && (dateRange.from || dateRange.to));

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2"
      data-testid={testId}
    >
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          data-testid={`${testId}-search`}
          type="text"
          value={search}
          onChange={(e) => onSearch && onSearch(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearch && onSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            data-testid={`${testId}-search-clear`}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {chips.map(chip => (
        <SearchableSelect
          key={chip.key}
          value={chip.value && chip.value !== 'all' ? chip.value : ''}
          onChange={(v) => onChipChange && onChipChange(chip.key, v || 'all')}
          options={[{ value: '', label: `${chip.label}: All` }, ...chip.options.map(o => ({ value: o.value, label: o.label }))]}
          placeholder={`${chip.label}: All`}
          testid={`${testId}-chip-${chip.key}`}
          allowClear
          minWidth={170}
        />
      ))}

      {dateRange && (
        <>
          <input
            type="date"
            value={dateRange.from || ''}
            onChange={(e) => onDateChange && onDateChange({ ...dateRange, from: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none"
            data-testid={`${testId}-date-from`}
            title="From"
          />
          <span className="text-gray-400 text-sm">→</span>
          <input
            type="date"
            value={dateRange.to || ''}
            onChange={(e) => onDateChange && onDateChange({ ...dateRange, to: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none"
            data-testid={`${testId}-date-to`}
            title="To"
          />
        </>
      )}

      {hasFilter && (
        <button
          type="button"
          onClick={onClear}
          className="flex items-center space-x-1 px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg"
          data-testid={`${testId}-clear`}
        >
          <X size={14} /><span>Clear</span>
        </button>
      )}

      {typeof resultCount === 'number' && (
        <span className="ml-auto inline-flex items-center text-xs text-gray-500" data-testid={`${testId}-count`}>
          <FilterIcon size={12} className="mr-1" />
          {resultCount} result{resultCount === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}
